import { Injectable } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signOut,
  user,
  updateProfile,
  browserPopupRedirectResolver,
  User as FirebaseAuthUser,
} from '@angular/fire/auth';
import {
  Firestore,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  docData,
  DocumentReference,
  docSnapshots,
} from '@angular/fire/firestore';
import { Observable, of } from 'rxjs';
import { switchMap, map, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { User } from '../../models/user.model';
import { UserStateService } from '../../state/user-state.service';
import { LoggerService } from '../common/logger.service';
import { UserOnboardingService } from './user-onboarding.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  currentUser$: Observable<User | null>;

  constructor(
    private auth: Auth,
    private firestore: Firestore,
    private router: Router,
    private userState: UserStateService,
    private logger: LoggerService,
    private userOnboardingService: UserOnboardingService,
  ) {
    this.currentUser$ = user(this.auth).pipe(
      switchMap((authUser: FirebaseAuthUser | null) => {
        if (authUser) {
          const userRef = doc(
            this.firestore,
            `users/${authUser.uid}`,
          ) as DocumentReference<User>;
          return docSnapshots(userRef).pipe(
            map(docSnap => {
              return docSnap.exists() ? docSnap.data() : null;
            }),
          );
        } else {
          return of(null);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.currentUser$.subscribe(profile => this.userState.setProfile(profile));
  }

  // Handle user sign in (both first-time and subsequent)
  private async userSignIn(firebaseUser: FirebaseAuthUser): Promise<boolean> {
    const userRef = doc(this.firestore, `users/${firebaseUser.uid}`);
    const docSnap = await getDoc(userRef);

    if (!docSnap.exists()) {
      // First-time user
      this.logger.info('New user detected, starting onboarding');
      await this.userOnboardingService.onboardUser(firebaseUser);
      this.router.navigate(['/newuser']);
      return true;
    } else {
      // Existing user - just update lastLoginAt
      this.logger.info('Existing user detected, updating lastLoginAt');
      await setDoc(
        userRef,
        { lastLoginAt: serverTimestamp() },
        { merge: true },
      );
      this.router.navigate(['/']);
      return false;
    }
  }

  // Google Authentication
  signInWithGoogle() {
    return signInWithPopup(
      this.auth,
      new GoogleAuthProvider(),
      browserPopupRedirectResolver,
    ).then(credential => this.userSignIn(credential.user));
  }

  // Apple Authentication
  signInWithApple() {
    const provider = new OAuthProvider('apple.com');
    return signInWithPopup(this.auth, provider).then(result =>
      this.userSignIn(result.user),
    );
  }

  // Update Profile
  updateUserProfile(profileData: { displayName?: string; photoURL?: string }) {
    const currentUser = this.auth.currentUser;
    if (!currentUser) return Promise.reject('No user logged in');
    return updateProfile(currentUser, profileData).then(() => {
      const userRef = doc(this.firestore, `users/${currentUser.uid}`);
      const firestoreData: Partial<User> = {};
      if (profileData.displayName !== undefined)
        firestoreData.displayName = profileData.displayName;
      if (profileData.photoURL !== undefined)
        firestoreData.photoURL = profileData.photoURL;
      return setDoc(userRef, firestoreData, { merge: true });
    });
  }

  // Sign Out
  signOut() {
    return signOut(this.auth).then(() => {
      this.logger.info('Signed out successfully');
      this.router.navigate(['/']);
    });
  }

  setDateOfBirth(date: string) {
    const currentUser = this.auth.currentUser;
    if (!currentUser) return Promise.reject('No user logged in');
    const userRef = doc(this.firestore, `users/${currentUser.uid}`);
    return setDoc(userRef, { dateOfBirth: date }, { merge: true });
  }
}
